import { useMemo } from "react";

import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import { type BadgeType } from "@components/Badge";
import getProductComboOptionByIdQuery from "@graphql/get-product-combo-option-by-id.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { useProduct } from "@react/hooks/useProduct";
import { type ProductSelectorSettings } from "@react/sections/ProductHero/ProductSelector/ProductSelectorContext";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  isVariantFixedBundle,
  isProductFixedBundle,
  isMigratedBundle,
} from "@react/utilities";
import { type CartProductConnection } from "@root/tablescape/state";
import { formatProduct, getShopifyGlobalId } from "@root/tablescape/utilities";
import { STOREFRONT_API_URL } from "@tablescape/constants";

type ProductComboOptionConnection = {
  data?: {
    metaobject: Maybe<{
      badge: Maybe<{
        reference: {
          type: Maybe<{ value: BadgeType }>;
        };
      }>;
      badgeText: Maybe<{ value: string }>;
      description: Maybe<{ value: string }>;
      products: {
        references: {
          nodes: CartProductConnection[];
        };
      };
      title: Maybe<{ value: string }>;
    }>;
  };
};

export const useProductComboOptionParams = [
  STOREFRONT_API_URL,
  getProductComboOptionByIdQuery,
];

export function useProductComboOption(metaobjectId: string | undefined) {
  const {
    meta: {
      localization: { country, language },
    },
    productId,
  } = useSectionSettings<ProductSelectorSettings>();

  const { data, isLoading } = useSWR<ProductComboOptionConnection>(
    metaobjectId && country && language
      ? [
          ...useProductComboOptionParams,
          {
            metaobjectId,
            country,
            language,
          },
        ]
      : null,
    storefrontAPIFetcher,
    {}
  );

  const productComboOption = data?.data?.metaobject;

  const products = useMemo(
    () =>
      productComboOption?.products?.references.nodes.map((product) =>
        formatProduct(product)
      ) || [],
    [productComboOption]
  );

  const isProductComboOptionProductFixedBundle = useMemo(() => {
    return !!products[0] && isProductFixedBundle(products[0]);
  }, [products]);

  const { product: firstProductFixedBundleComponent } = useProduct(
    products[0]?.productFixedBundleComponents
      ? products[0]?.productFixedBundleComponents[0]?.id
      : undefined
  );

  const { product: secondProductFixedBundleComponent } = useProduct(
    products[0]?.productFixedBundleComponents
      ? products[0]?.productFixedBundleComponents[1]?.id
      : undefined
  );

  const comboProducts = useMemo(() => {
    if (
      isProductComboOptionProductFixedBundle &&
      firstProductFixedBundleComponent &&
      secondProductFixedBundleComponent
    ) {
      return [
        firstProductFixedBundleComponent,
        secondProductFixedBundleComponent,
      ];
    }
    return [
      ...(products[0] ? [products[0]] : []),
      ...(products[1] ? [products[1]] : []),
    ];
  }, [
    firstProductFixedBundleComponent,
    isProductComboOptionProductFixedBundle,
    products,
    secondProductFixedBundleComponent,
  ]);

  const isProductComboOptionVariantFixedBundle = useMemo(
    () => !!products[0] && isVariantFixedBundle(products[0].variants),
    [products]
  );

  const isProductComboOptionMigratedBundle = useMemo(
    () => !!products[0] && isMigratedBundle(products[0]),
    [products]
  );

  const showThumbnail = useMemo(
    () =>
      isProductComboOptionMigratedBundle ||
      isProductComboOptionVariantFixedBundle ||
      comboProducts.length > 1,
    [
      isProductComboOptionMigratedBundle,
      isProductComboOptionVariantFixedBundle,
      comboProducts,
    ]
  );

  return {
    badgeText: productComboOption?.badgeText?.value,
    badgeType: productComboOption?.badge?.reference.type?.value,
    description:
      productComboOption?.description?.value || products[0]?.description,
    isProductFixedBundle: isProductComboOptionProductFixedBundle,
    isCurrentPdpProduct:
      products.length === 1 &&
      products[0].id === getShopifyGlobalId("Product", productId),
    isVariantFixedBundle: isProductComboOptionVariantFixedBundle,
    isMigratedBundle: isProductComboOptionMigratedBundle,
    isLoading,
    originalProducts: products,
    products: comboProducts,
    showThumbnail,
    title:
      productComboOption?.title?.value ||
      products.map((product) => product.title).join(" + "),
  };
}
